import { ActionTypes } from "./actions";
import { produce } from "immer";
import { requestIsBusy, requestResponse } from "@redriver/cinnamon";
import { SitePermissionItemType } from "constants/enums";

const initialState = {
  groupId: null,
  permissionData: [],
  loading: false,
  expandedIds: {},
  loadingIds: {},
  originalValues: {},
  customisedValues: {},
  saving: false,
};

const getNode = (id, parentIds, defaultItems) => {
  const ids = [...parentIds, id];
  let item = null;
  while (ids.length > 0) {
    item =
      (item == null ? defaultItems : item.items).find((x) => x.id == ids[0]) ||
      item;
    ids.shift();
  }
  return item;
};

const copyOriginalValues = (item, depth = 0, override = null) => {
  let result = {
    [item.id]: {
      inScope: override !== null ? override : !!item.inScope,
      hasPartialChildScope:
        override !== null ? false : !!item.hasPartialChildScope,
      depth: depth || 0,
      type: item.type,
    },
  };

  const nextDepth = result[item.id].depth + 1;

  if (Array.isArray(item.items) && item.items.length > 0) {
    result = item.items.reduce(
      (acc, x) => ({
        ...acc,
        ...copyOriginalValues(x, nextDepth, override),
      }),
      result
    );
  }
  return result;
};

const calcCustomScope = (
  item,
  draft,
  inScope,
  shouldPropagate = false,
  markDirty = false
) => {
  if (shouldPropagate) {
    item.items.forEach((x) => {
      calcCustomScope(x, draft, inScope, true);
      draft[x.id].shouldPropagate = true;
      if (markDirty) draft[x.id].isDirty = true;
    });
  }

  const isDirectoryWithChildren =
    item.type != SitePermissionItemType.File &&
    item.hasChildItems &&
    item.items.length > 0;

  draft[item.id] = {
    inScope,
    hasPartialChildScope: isDirectoryWithChildren
      ? item.items.some(
          (x) => draft[x.id].inScope || draft[x.id].hasPartialChildScope
        ) &&
        !item.items.every(
          (x) => draft[x.id].inScope && !draft[x.id].hasPartialChildScope
        )
      : false,
    depth: draft[item.id].depth,
    type: draft[item.id].type,
    shouldPropagate: shouldPropagate || !!draft[item.id].shouldPropagate,
  };

  if (markDirty) {
    draft[item.id].isDirty = true;
  }
};

export default (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.ListGroupPermissions: {
      const response = requestResponse(action);
      const loading = requestIsBusy(action);

      // Response is null whenever the request isn't a success, which includes
      // failed and aborted requests, not just ones still in flight
      const loaded = !loading && Array.isArray(response);

      const originalValues = loaded
        ? response.reduce(
            (acc, x) => ({ ...acc, ...copyOriginalValues(x, 0) }),
            {}
          )
        : {};
      return {
        ...state,
        loading: loading,
        // Permission data and the value lookups have to stay in step, otherwise
        // rows render against values that were never copied - so a load in
        // progress clears both rather than holding rows for a different group
        permissionData: loaded ? response : [],
        groupId: action.customData.groupId,
        expandedIds: {},
        loadingIds: {},
        originalValues: originalValues,
        customisedValues: originalValues,
      };
    }
    case ActionTypes.SetExpandedPermissionNode:
      return {
        ...state,
        expandedIds: {
          ...state.expandedIds,
          [action.nodeId]: action.value,
        },
      };

    // Get the children of a permission item and insert it into the existing data
    case ActionTypes.ListGroupPermissionChildren: {
      const response = requestResponse(action);
      const loading = requestIsBusy(action);
      const nodeId = action.customData.nodeId;
      const parentIds = action.customData.parentIds || [];
      const ids = [...parentIds, nodeId];

      // Response is null for failed/aborted requests and can come back as an
      // empty array, so nothing below can assume there is a parent to read
      const parent =
        !loading && Array.isArray(response) && response.length > 0
          ? response[0]
          : null;
      const parentOriginal = parent ? state.originalValues[parent.id] : null;
      const parentCustomised = parent
        ? state.customisedValues[parent.id]
        : null;
      const childItems =
        parent && Array.isArray(parent.items) ? parent.items : [];

      // Child rows and their scope values are applied together — adding rows
      // without their values leaves the list rendering against missing lookups
      const applyChildren =
        !!parentOriginal && !!parentCustomised && childItems.length > 0;

      const nextPermissionData = !applyChildren
        ? state.permissionData
        : produce(state.permissionData, (draft) => {
            let item = null;
            // Move through list of ids to identify where the item we are interested in updating
            while (ids.length > 0) {
              item =
                (item == null ? draft : item.items).find(
                  (x) => x.id == ids[0]
                ) || item;
              ids.shift();
            }

            if (item && item.items.length == 0) {
              item.items = childItems;
            }

            return draft;
          });

      const parentValueHasChanged = applyChildren
        ? parentCustomised.inScope != parentOriginal.inScope
        : false;
      const overrideValue = parentValueHasChanged
        ? parentCustomised.inScope
        : null;

      return {
        ...state,
        permissionData: nextPermissionData,
        loadingIds: {
          ...state.loadingIds,
          [nodeId]: loading,
        },
        originalValues: applyChildren
          ? childItems.reduce((acc, x) => {
              const depth = parentOriginal.depth + 1;
              return {
                ...acc,
                ...copyOriginalValues(x, depth),
              };
            }, state.originalValues)
          : state.originalValues,
        customisedValues: applyChildren
          ? childItems.reduce((acc, x) => {
              const depth = parentCustomised.depth + 1;
              const shouldPropagate = !!parentCustomised.shouldPropagate;

              const copiedValues = copyOriginalValues(x, depth, overrideValue);
              if (shouldPropagate) {
                // Pass propagation flag down to new children
                Object.keys(copiedValues).forEach((k) => {
                  copiedValues[k].shouldPropagate = true;
                });
              }

              return {
                ...acc,
                ...copiedValues,
              };
            }, state.customisedValues)
          : state.customisedValues,
      };
    }

    case ActionTypes.SetInScopeForPermissionNode: {
      const nodeId = action.nodeId;
      const parentIds = action.parentIds || [];
      const ids = [...parentIds, nodeId];

      // find current item
      let item = null;
      while (ids.length > 0) {
        item =
          (item == null ? state.permissionData : item.items).find(
            (x) => x.id == ids[0]
          ) || item;
        ids.shift();
      }

      if (!item) return state;

      const nextCustomValues = produce(state.customisedValues, (draft) => {
        // Update item's own value record and propagate to children
        calcCustomScope(item, draft, action.value, true, true);

        [...parentIds].reverse().forEach((id, index) => {
          const node = getNode(
            id,
            parentIds.slice(0, parentIds.length - index),
            state.permissionData
          );

          calcCustomScope(node, draft, true, false, true);
        });
      });

      return {
        ...state,
        customisedValues: nextCustomValues,
      };
    }

    case ActionTypes.UpdatePermissions: {
      const saving = requestIsBusy(action);
      return {
        ...state,
        saving: saving,
      };
    }

    default:
      return state;
  }
};
